import type { Id, ISODate, Version } from './common';

/**
 * Per-item sharing (docs/specs/done/2026-09-28-sharing-design.md). Every home — a project, or a root with no
 * project — has one audience: `private` is its owner alone, `shared` its live members, `public` every account of
 * the company. A child is seen by whoever sees its root.
 */
export const SHARING_MODES = ['private', 'shared', 'public'] as const;
export type SharingMode = typeof SHARING_MODES[number];

/**
 * How a new root without a project is shared (§8.2): a mode and, for `shared` only, the people besides its owner.
 * `revive` is the undo of deleting a standalone root: the deleted root's own audience comes back.
 */
export type SharingCreateInput = { mode: SharingMode; memberIds?: Id[] } | { revive: Id };

/** The home whose audience an item follows. `restricted`: the viewer cannot open that project, so the web never links it. */
export type SharingHome = { kind: 'item' | 'project'; id: Id; title: string; restricted: boolean };

/** One live member. `active` false reads "<tên> (ngừng hoạt động)" on its chip. */
export type SharingMemberDTO = { id: Id; name: string; addedAt: ISODate; active: boolean };

export type SharingDTO = {
  mode: SharingMode; owner: { id: Id; name: string }; members: SharingMemberDTO[];
  version: Version; home: SharingHome; can: { add: boolean; manage: boolean; changeHome: boolean };
};

export type SharingModeBody = { mode: SharingMode; version: Version };
export type SharingMembersBody = { userIds: Id[] };

/** The people a save added to the home's audience so they could see what they were given (§8.3). */
export type SharingEffect = { addedToSharing: Id[] };

/** `POST /api/workplace/items/:id/home` (§8.4). */
export type ItemHomeBody = { projectId: Id } | { parentId: Id } | { standalone: true };

/** What a board card says about its home's sharing; `ownerId` lets the card menu offer "Đổi nhà…" without a fetch. */
export type BoardCardSharing = { mode: SharingMode; memberCount: number; ownerId: Id };

/** Why a person joined an audience, as the `member_add` audit records it (§8.6). */
export type MemberVia = 'direct' | 'assignment' | 'manager' | 'home_change' | 'replacement' | 'mode_change';
